import { useState, useCallback, useMemo } from "react";
import { useEmbeddedEnvironment } from "./useEmbeddedEnvironment";

export function useEmbeddedExitConfirm() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingHref, setPendingHref] = useState(null);
  const [pendingCallback, setPendingCallback] = useState(null);
  const { isEmbeddedEnvironment, brandName, storagePrefix } =
    useEmbeddedEnvironment();

  const shouldShowConfirmation = useCallback(() => {
    if (!isEmbeddedEnvironment) return false;

    try {
      const disabled =
        localStorage?.getItem(`${storagePrefix}ExitConfirmDisabled`) === "true";
      return !disabled;
    } catch (error) {
      console.warn(
        "Failed to access localStorage for exit confirmation:",
        error
      );
      return true;
    }
  }, [isEmbeddedEnvironment, storagePrefix]);

  const memoizedShouldShow = useMemo(
    () => shouldShowConfirmation(),
    [shouldShowConfirmation]
  );

  const interceptExternalLink = useCallback(
    (href, target = "_blank", originalCallback = null) => {
      if (!shouldShowConfirmation() || target !== "_blank") {
        if (originalCallback) {
          originalCallback();
        } else {
          window.open(href, target);
        }
        return;
      }

      setPendingHref(href);
      setPendingCallback(originalCallback);
      setDialogOpen(true);
    },
    [shouldShowConfirmation]
  );

  const handleConfirm = useCallback(() => {
    setDialogOpen(false);

    if (pendingCallback) {
      pendingCallback();
    } else if (pendingHref) {
      window.open(pendingHref, "_blank");
    }

    setPendingHref(null);
    setPendingCallback(null);
  }, [pendingCallback, pendingHref]);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
    setPendingHref(null);
    setPendingCallback(null);
  }, []);

  const createClickHandler = useCallback(
    (href, target = "_blank", originalOnClick = null) => {
      return (event) => {
        if (originalOnClick) {
          originalOnClick(event);
        }

        if (shouldShowConfirmation() && target === "_blank") {
          event.preventDefault();
          interceptExternalLink(href, target);
          return false;
        }
      };
    },
    [shouldShowConfirmation, interceptExternalLink]
  );

  return {
    dialogOpen,
    handleConfirm,
    handleCancel,
    interceptExternalLink,
    createClickHandler,
    shouldShowConfirmation: memoizedShouldShow,
    brandName,
    storagePrefix,
  };
}
